import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { Button, Checkbox, Form, FormProps, Input, Modal } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { CloudUploadOutlined } from '@ant-design/icons';

import ConfirmationPDF from '../confirmation-pdf';
import useModal from 'stores/useModal';
import useBookingStore from 'stores/useBooking';

import { uploadConfirmation } from 'services/api/module/booking.api';
import { getBase64 } from 'utils';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { updateOrCreateBookingLineGuests } from 'services/api/module/customer.api';
import { useTranslation } from 'react-i18next';
import useCommonStore from 'stores/useCommon';
import NikConfirmationPDF from '../confirmation-pdf/nik';

export enum CHARGE_TYPE {
  ALL = 'all',
  ROOM = 'room',
  SERVICE = 'service'
}

function ConfirmationPDFModal() {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const { isOpenConfirmationPDF, setIsOpenConfirmationPDF, setIsOpenSignature } = useModal();
  const { bookingLineDetail } = useBookingStore();
  const { currentBranch } = useCommonStore();
  const mainGuest = bookingLineDetail?.booking_line_guests?.find(item => item.main_guest);
  const mainGuestEmail = mainGuest?.email || '';
  const mainGuestPhone = mainGuest?.phone || '';

  const [pdfData, setPdfData] = useState({
    isShowRoomRate: false,
    isShowVAT: false,
    isShowMainGuest: true,
    isShowGuest: false,
    company: '',
    paymentMethod: '',
    email: mainGuestEmail,
    phone: mainGuestPhone
  });

  const [formData, setFormData] = useState({
    isShowRoomRate: false,
    isShowVAT: false,
    isShowMainGuest: true,
    isShowGuest: false,
    company: '',
    paymentMethod: '',
    email: mainGuestEmail,
    phone: mainGuestPhone
  });

  const [signatures, setSignatures] = useState({
    staff: '',
    guest: ''
  });

  const { mutateAsync: mutateUploadConfirmation, isPending } = useMutation({
    mutationFn: async ({ bookingLineId, file, fileName }: any) => {
      const customers = await uploadConfirmation(bookingLineId, {
        file,
        name: fileName
      });
      return customers;
    }
  });

  useEffect(() => {
    if (isOpenConfirmationPDF) {
      const initialData = {
        isShowRoomRate: false,
        isShowVAT: false,
        isShowMainGuest: true,
        isShowGuest: false,
        company: '',
        paymentMethod: '',
        email: mainGuestEmail,
        phone: mainGuestPhone
      };

      form.setFieldsValue(initialData);
      setFormData(initialData);
      setPdfData(initialData);
      setSignatures({ staff: '', guest: '' });
    }
  }, [form, mainGuestEmail, mainGuestPhone, isOpenConfirmationPDF]);

  const handleCancel = () => setIsOpenConfirmationPDF(false);

  const handleSignature = (type: 'staff' | 'guest') => {
    setIsOpenSignature(true, {
      title: type === 'staff' ? t('common.signature.staff') : t('common.signature.customer'),
      onOk: (data: string) => {
        setSignatures(prev => ({ ...prev, [type]: data }));
      }
    });
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    const { isShowGuest, isShowMainGuest } = values;

    if (isShowMainGuest || isShowGuest) {
      const isMain = isShowMainGuest;
      const guest = bookingLineDetail?.booking_line_guests.find(item => item.main_guest === isMain);

      const email = values.email || guest?.email || '';
      const phone = values.phone || guest?.phone || '';
      values.email = email;
      values.phone = phone;

      if (bookingLineDetail) {
        const profileData = bookingLineDetail.booking_line_guests.map((item: any) => ({
          profile_id: item.profile_id,
          name: item.guest_name,
          phone: item.main_guest === isMain ? phone : item.phone || '',
          email: item.main_guest === isMain ? email : item.email || '',
          guest: {
            main_guest: item.main_guest,
            is_child: item.is_child,
            guest_id: item.id
          }
        }));

        await updateOrCreateBookingLineGuests({
          booking_line_id: bookingLineDetail?.booking_line_id,
          profiles: profileData,
          adult: bookingLineDetail?.adult,
          child: bookingLineDetail?.child
        });

        const queryKeys = [
          [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, bookingLineDetail?.booking_line_id],
          [QUERY_KEYS.GET_GUEST],
          [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
        ];

        await Promise.all(queryKeys.map(key => queryClient.invalidateQueries({ queryKey: key })));
      }
    }

    setPdfData({
      ...values,
      email: values.email || '',
      phone: values.phone || ''
    });

    setFormData({
      ...values,
      email: values.email || '',
      phone: values.phone || ''
    });
  };

  const handleUpload = async (blob: Blob | null) => {
    if (!blob || !bookingLineDetail?.booking_line_id) return;

    const fileName = `confirmation_${bookingLineDetail.booking_line_id}-${dayjs().format('DD-MM-YYYY-HH-mm-ss')}`;
    const file = new File([blob], fileName, { type: 'application/pdf;charset=UTF-8' });

    try {
      const fileBase64 = await getBase64(file);
      await mutateUploadConfirmation({
        bookingLineId: bookingLineDetail.booking_line_id,
        file: fileBase64,
        fileName
      });

      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.KEY_ROOM,
          QUERY_KEYS.GET_BOOKING_DETAIL,
          bookingLineDetail.booking_line_id
        ]
      });
    } catch (error) {
      console.error('Error uploading confirmation:', error);
    }
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    let updatedFormData = { ...formData, [field]: checked };

    if (checked && (field === 'isShowMainGuest' || field === 'isShowGuest')) {
      const otherField = field === 'isShowMainGuest' ? 'isShowGuest' : 'isShowMainGuest';
      updatedFormData = { ...updatedFormData, [otherField]: false };
    }

    setFormData(updatedFormData);
    form.setFieldsValue({ [field]: checked });

    if (checked && (field === 'isShowMainGuest' || field === 'isShowGuest')) {
      const otherField = field === 'isShowMainGuest' ? 'isShowGuest' : 'isShowMainGuest';
      form.setFieldsValue({ [otherField]: false });
    }

    if (field === 'isShowMainGuest' || field === 'isShowGuest') {
      const isMain = field === 'isShowMainGuest' ? checked : updatedFormData.isShowMainGuest;
      const guest = bookingLineDetail?.booking_line_guests?.find(
        item => item.main_guest === isMain
      );

      if (guest) {
        const guestEmail = guest.email || '';
        const guestPhone = guest.phone || '';

        form.setFieldsValue({
          email: guestEmail,
          phone: guestPhone
        });

        updatedFormData.email = guestEmail;
        updatedFormData.phone = guestPhone;
        setFormData(updatedFormData);

        setPdfData(prev => ({
          ...prev,
          [field]: checked,
          [field === 'isShowMainGuest' ? 'isShowGuest' : 'isShowMainGuest']: false,
          email: guestEmail,
          phone: guestPhone
        }));
      } else {
        setPdfData(prev => ({
          ...prev,
          [field]: checked,
          [field === 'isShowMainGuest' ? 'isShowGuest' : 'isShowMainGuest']: false
        }));
      }
    } else {
      setPdfData(prev => ({
        ...prev,
        [field]: checked
      }));
    }
  };

  return (
    <Modal
      title={t('cashierPage.previewPrint')}
      className="modal-view-confirmation-pdf"
      centered
      width={1200}
      open={isOpenConfirmationPDF}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <PDFViewer className="confirmation-pdf-file">
        {currentBranch?.is_nik_hotel ? (
          <NikConfirmationPDF
            dataChange={pdfData}
            bookingLineDetail={bookingLineDetail}
            signatureStaff={signatures.staff}
            signatureGuest={signatures.guest}
          />
        ) : (
          <ConfirmationPDF
            dataChange={pdfData}
            bookingLineDetail={bookingLineDetail}
            signatureStaff={signatures.staff}
            signatureGuest={signatures.guest}
          />
        )}
      </PDFViewer>

      <Form
        form={form}
        name="confirmation-pdf-form"
        layout="horizontal"
        style={{ width: '250px' }}
        initialValues={formData}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          name="isShowRoomRate"
          label={t('cashierPage.confirmationPDF.showRoomRate')}
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
        <Form.Item
          name="isShowVAT"
          label={t('cashierPage.confirmationPDF.showVAT')}
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
        <Form.Item
          name="isShowMainGuest"
          label={t('cashierPage.confirmationPDF.showMainGuest')}
          valuePropName="checked"
        >
          <Checkbox onChange={e => handleCheckboxChange('isShowMainGuest', e.target.checked)} />
        </Form.Item>
        <Form.Item
          name="isShowGuest"
          label={t('cashierPage.confirmationPDF.showGuest')}
          valuePropName="checked"
        >
          <Checkbox onChange={e => handleCheckboxChange('isShowGuest', e.target.checked)} />
        </Form.Item>

        <Form.Item
          name="company"
          label={t('cashierPage.confirmationPDF.company')}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="paymentMethod"
          label={t('cashierPage.confirmationPDF.paymentMethod')}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
        <Form.Item name="email" label="Email" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
          <Input />
        </Form.Item>
        <Form.Item
          name="phone"
          label={t('cashierPage.confirmationPDF.phone')}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          style={{ marginBottom: 24 }}
        >
          <Input />
        </Form.Item>

        <Button htmlType="submit" className="ant-btn-secondary w-full">
          {t('managePage.travelAgencyPage.taActions.saveButton')}
        </Button>

        <div className="flex" style={{ flexDirection: 'column', gap: '8px', marginTop: 16 }}>
          <Button onClick={() => handleSignature('staff')}>{t('common.signature.staff')}</Button>
          <Button onClick={() => handleSignature('guest')}>{t('common.signature.customer')}</Button>
        </div>

        <div style={{ marginTop: 16 }}>
          <PDFDownloadLink
            document={
              <ConfirmationPDF
                dataChange={pdfData}
                bookingLineDetail={bookingLineDetail}
                signatureStaff={signatures.staff}
                signatureGuest={signatures.guest}
              />
            }
          >
            {({ blob, loading }) => (
              <Button
                className="ant-btn-secondary w-full"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleUpload(blob);
                }}
                loading={isPending || loading}
                icon={<CloudUploadOutlined />}
                disabled={loading}
              >
                {t('common.actions.upload')}
              </Button>
            )}
          </PDFDownloadLink>
        </div>
      </Form>
    </Modal>
  );
}

export default ConfirmationPDFModal;
